import { create } from 'zustand';
import { authorizeChat } from 'api/chat';
import { ChatUser } from 'types';
import i18next from 'i18next';
import { getUserData, UserData } from 'api/user';

interface BetterAuthUser {
  id: string;
  email: string;
  name?: string;
  emailVerified: boolean;
}

interface UserState {
  userData: UserData | null;
  chatUser: ChatUser | null;
  isFetchingUser: boolean;
  fetchError: string | null;
  isAuthenticated: boolean;
  betterAuthUser: BetterAuthUser | null;
  updateUserData: (updatedUserData: Partial<UserData['me']>) => void;
  fetchUserData: (user: BetterAuthUser) => Promise<void>;
  setIsAuthenticated: (value: boolean) => void;
  setBetterAuthUser: (user: BetterAuthUser | null) => void;
  clearUserData: () => void;
}

export const useBetterAuthUserStore = create<UserState>()((set, getState) => ({
  userData: null,
  chatUser: null,
  isFetchingUser: false,
  fetchError: null,
  isAuthenticated: false,
  betterAuthUser: null,
  updateUserData: (updatedUserData) => {
    const me = getState().userData?.me;
    set({
      userData: { ...getState().userData, me: { ...me!, ...updatedUserData } },
    });
  },
  fetchUserData: async (user) => {
    if (getState().isFetchingUser || getState().userData) {
      return;
    }

    set({ isFetchingUser: true, betterAuthUser: user });

    try {
      const [meRes, chatUserRes] = await Promise.allSettled([
        getUserData(),
        authorizeChat(),
      ]);
      const isMeFulfilled = meRes.status === 'fulfilled';
      const isChatUserFulfilled = chatUserRes.status === 'fulfilled';

      const newState = {
        isFetchingUser: false,
        ...(isMeFulfilled && { userData: meRes.value }),
        ...(isChatUserFulfilled &&
          isMeFulfilled && {
            chatUser: {
              id: meRes.value.me.userId ?? '',
              name: meRes.value.me.nickname ?? user.name ?? user.email,
              image: meRes.value.me.avatarUrl ?? '',
              token: chatUserRes.value,
            },
          }),
        ...(!isMeFulfilled && {
          fetchError: i18next.t(
            'core:user-settings-view.user-profile.unable-to-fetch-user'
          ),
        }),
      };

      set(newState);
    } catch (error) {
      console.error('❌ UserStore: Error fetching user data:', error);
      set({
        isFetchingUser: false,
        fetchError: 'Failed to fetch user data',
      });
    }
  },
  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
  setBetterAuthUser: (user) => set({ betterAuthUser: user }),
  clearUserData: () =>
    set({
      userData: null,
      chatUser: null,
      betterAuthUser: null,
      isAuthenticated: false,
      fetchError: null,
    }),
}));

export const useMe = () =>
  useBetterAuthUserStore((state) => state.userData?.me);
